import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ProjectionValue } from '@/components/px/ProjectionValue'
import { SectionLabel } from '@/components/px/SectionLabel'
import { projectNav } from '@/components/px/nav'
import { selectRoleKey } from '@/features/auth/slices/sessionSlice'
import { ORG_ADMIN_ROLE_KEYS } from '@/access/roleMapping'
import {
  budgetsErrorDismissed,
  fetchActiveBudget,
  fetchBudgetHistory,
  fetchBudgetVsActual,
  putBudgetRevision,
} from '@/features/budgets/slices/budgetsSlice'
import {
  selectActiveBudget,
  selectActiveBudgetMissing,
  selectBudgetHistory,
  selectBudgetsError,
  selectBudgetsLoading,
  selectBudgetsMutating,
  selectBudgetVsActual,
} from '@/features/budgets/selectors'
import type { PutBudgetInput } from '@/features/budgets/types'
import { formatDay, formatMinutes, formatMoney, formatPct } from '@/lib/money'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * PROJECT BUDGET (`/project/:projectId/budget`, PX-W6.4 AC-4): the active
 * revision, its history, the revision form (admin-gated, HIDDEN for others)
 * and the budget-versus-actual roll-up.
 *
 * Every figure the page shows is the server's projection VERBATIM —
 * `actualCostMinorTotal`, `remainingMinor`, `consumedPct` and the
 * per-package pair render exactly as returned, never recomputed from line
 * items, never re-summed. A 404 on the active budget is the explicit "no
 * budget agreed" fact (I-7), never an error and never zero. A negative
 * `remainingMinor` is an overrun and renders as-is. The cost cells go
 * through `ProjectionValue`: an absent key (CLIENT side, or `hasBudget:false`)
 * renders the "not shared" placeholder.
 */

/** The revision form's draft; money is a major-unit string until submit. */
interface RevisionDraft {
  amount: string
  currency: string
  hours: string
  note: string
}

const CURRENCIES: readonly string[] = ['EUR', 'USD', 'GBP', 'CHF']

export default function ProjectBudgetPage() {
  const { t } = useTranslation('commercial')
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const roleKey = useAppSelector(selectRoleKey)

  const active = useAppSelector(selectActiveBudget)
  const activeMissing = useAppSelector(selectActiveBudgetMissing)
  const history = useAppSelector(selectBudgetHistory)
  const view = useAppSelector(selectBudgetVsActual)
  const loading = useAppSelector(selectBudgetsLoading)
  const error = useAppSelector(selectBudgetsError)
  const mutating = useAppSelector(selectBudgetsMutating)

  const mayRevise = roleKey !== null && ORG_ADMIN_ROLE_KEYS.includes(roleKey)

  const [draft, setDraft] = useState<RevisionDraft>({ amount: '', currency: CURRENCIES[0]!, hours: '', note: '' })
  // Tracks the `active` reference the form was last seeded from, so the
  // render-time adjustment below fires exactly once per new value.
  const [seededActive, setSeededActive] = useState<typeof active>(null)
  const [formError, setFormError] = useState<string | null>(null)

  // Three reads on mount — each guarded by its own cache.
  useEffect(() => {
    if (projectId === undefined) return
    void dispatch(fetchActiveBudget(projectId))
    void dispatch(fetchBudgetHistory(projectId))
    void dispatch(fetchBudgetVsActual(projectId))
  }, [dispatch, projectId])

  // Seed the revision form from the active budget once it lands. Adjusted
  // during RENDER, not an Effect — the React "adjusting state when a value
  // changes" pattern: comparing against the last-seeded reference re-seeds
  // exactly once per new `active` value, with no extra render round-trip
  // and no cascading setState-in-effect.
  if (active !== seededActive) {
    setSeededActive(active)
    if (active !== null) {
      setDraft({
        amount: String(active.amountMinor / 100),
        currency: active.currency,
        hours: active.hoursPlanned === undefined ? '' : String(active.hoursPlanned),
        note: active.note ?? ''
      })
    }
  }

  const onRetryClicked = (): void => {
    if (projectId === undefined) return
    dispatch(budgetsErrorDismissed())
    void dispatch(fetchActiveBudget(projectId))
    void dispatch(fetchBudgetHistory(projectId))
    void dispatch(fetchBudgetVsActual(projectId))
  }

  const onRevisionSaved = (): void => {
    if (projectId === undefined) return
    const amount = Number(draft.amount)
    if (draft.amount === '' || !Number.isFinite(amount) || amount < 0) {
      setFormError(t('commercial:budget.form.invalid'))
      return
    }
    const hours = draft.hours.trim() === '' ? undefined : Number(draft.hours)
    if (hours !== undefined && (!Number.isInteger(hours) || hours < 0)) {
      setFormError(t('commercial:budget.form.invalid'))
      return
    }
    const input: PutBudgetInput = {
      // Full replace of the REVISION — omitted fields are absent from the new
      // revision, they do not fall back to the previous one.
      amountMinor: Math.round(amount * 100),
      currency: draft.currency,
      hoursPlanned: hours,
      note: draft.note.trim() === '' ? undefined : draft.note.trim()
    }
    setFormError(null)
    void dispatch(putBudgetRevision({ projectId, input }))
  }

  const inputClass =
    'rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

  // PX-W6.6: the project rail follows the ROUTE, not a demo hero — pointing it
  // at the seed project made every engagement's budget page link to another's.
  const rail = { id: projectId ?? '', client: view?.projectName ?? projectId ?? '' }
  const viewCurrency = view?.costCurrency ?? view?.currency

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.project')}
      breadcrumb={<span>{t('commercial:budget.title')}</span>}
      contextPill={(view?.projectName ?? projectId ?? '—').toUpperCase()}
      nav={projectNav(rail)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('commercial:budget.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('commercial:budget.lede')}</p>
      </header>

      <div className="mt-5 space-y-4">
        {loading ? (
          <LoadingPanel />
        ) : error !== null ? (
          <ErrorPanel
            error={{ status: error.status ?? 0, message: error.message, backendKey: error.key }}
            onRetryClicked={onRetryClicked}
          />
        ) : (
          <>
            {/* ── The active revision, or the explicit no-budget fact ─────── */}
            <section aria-label={t('commercial:budget.active.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
              <SectionLabel>{t('commercial:budget.active.title')}</SectionLabel>
              {activeMissing ? (
                <div className="mt-2">
                  <EmptyPanel
                    icon={undefined}
                    title={t('commercial:budget.active.none')}
                    body={t('commercial:budget.active.noneBody')}
                  />
                </div>
              ) : active === null ? (
                <p className="mt-2 text-sm text-muted-foreground">—</p>
              ) : (
                <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm lg:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.active.amount')}
                    </dt>
                    <dd className="v-figure mt-0.5 font-serif text-lg font-semibold tabular-nums text-foreground">
                      <ProjectionValue
                        value={active.amountMinor}
                        format={(n) => formatMoney(n, active.currency)}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.active.revision')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">#{active.revision}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.active.hoursPlanned')}
                    </dt>
                    <dd className="mt-0.5 text-foreground">
                      {active.hoursPlanned === undefined
                        ? t('commercial:budget.active.noHoursPlan')
                        : `${active.hoursPlanned} h`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.active.note')}
                    </dt>
                    <dd className="mt-0.5 text-muted-foreground">{active.note ?? '—'}</dd>
                  </div>
                </dl>
              )}
            </section>

            {/* ── Budget versus actual — server figures, verbatim ─────────── */}
            <section aria-label={t('commercial:budget.vsActual.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
              <SectionLabel>{t('commercial:budget.vsActual.title')}</SectionLabel>
              {view === null ? (
                <p className="mt-2 text-sm text-muted-foreground">—</p>
              ) : (
                <>
                  {/* hasBudget:false keeps the HOURS half (I-7): the absence is
                      a fact about the budget, not about the submitted work. */}
                  {view.hasBudget === false && (
                    <p className="mt-2 rounded-md border border-dashed border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                      {t('commercial:budget.vsActual.noBudget')} —{' '}
                      {t('commercial:budget.vsActual.hoursOnlyNote')}
                    </p>
                  )}
                  <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm lg:grid-cols-4">
                    <div>
                      <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                        {t('commercial:budget.vsActual.actualHours')}
                      </dt>
                      <dd className="mt-0.5 text-foreground">
                        {formatMinutes(view.actualHoursMinutes)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                        {t('commercial:budget.vsActual.entries')}
                      </dt>
                      <dd className="mt-0.5 text-foreground">{view.entryCount}</dd>
                    </div>
                    {view.hasBudget && (
                      <>
                        <div>
                          <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                            {t('commercial:budget.vsActual.agreed')}
                          </dt>
                          <dd className="mt-0.5 text-foreground">
                            <ProjectionValue
                              value={view.budgetAmountMinor}
                              format={(n) => formatMoney(n, view.currency)}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                            {t('commercial:budget.vsActual.actualCost')}
                          </dt>
                          <dd className="mt-0.5 text-foreground">
                            <ProjectionValue
                              value={view.actualCostMinorTotal}
                              format={(n) => formatMoney(n, viewCurrency)}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                            {t('commercial:budget.vsActual.remaining')}
                          </dt>
                          <dd
                            className={`mt-0.5 font-semibold tabular-nums ${
                              view.remainingMinor !== undefined && view.remainingMinor < 0
                                ? 'text-blocked'
                                : 'text-foreground'
                            }`}
                          >
                            {/* Negative = overrun: rendered as-is, never clamped. */}
                            <ProjectionValue
                              value={view.remainingMinor}
                              format={(n) => formatMoney(n, viewCurrency)}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                            {t('commercial:budget.vsActual.consumed')}
                          </dt>
                          <dd className="mt-0.5 text-foreground">
                            <ProjectionValue value={view.consumedPct} format={formatPct} />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase tracking-[0.06em] text-muted-foreground">
                            {t('commercial:budget.vsActual.unrated')}
                          </dt>
                          <dd className="mt-0.5 text-foreground">
                            <ProjectionValue value={view.unratedMinutes} format={formatMinutes} />
                          </dd>
                        </div>
                      </>
                    )}
                  </dl>
                </>
              )}

              {view?.perWorkPackage !== undefined && view.perWorkPackage.length > 0 && (
                <div className="mt-4 overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="v-row border-b border-border bg-secondary">
                        <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.package')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.plannedAmount')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.plannedHours')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.actualMinutes')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.actualCost')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('commercial:budget.vsActual.packageConsumed')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {view.perWorkPackage.map((row) => (
                        <tr key={row.workPackageKey} className="v-row border-b border-border last:border-b-0">
                          <td className="px-3 py-2.5 text-foreground">{row.workPackageKey}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                            <ProjectionValue
                              value={row.plannedAmountMinor}
                              format={(n) => formatMoney(n, view.currency)}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                            {row.plannedHours === undefined ? '—' : `${row.plannedHours} h`}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                            {formatMinutes(row.actualMinutes)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <ProjectionValue
                              value={row.actualCostMinor}
                              format={(n) => formatMoney(n, viewCurrency)}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <ProjectionValue value={row.consumedPct} format={formatPct} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ── The revision form — admin lens only, HIDDEN otherwise ───── */}
            {mayRevise && (
              <section aria-label={t('commercial:budget.form.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
                <SectionLabel>{t('commercial:budget.form.title')}</SectionLabel>
                <p className="mt-1 text-xs text-muted-foreground">{t('commercial:budget.form.note')}</p>
                <div className="mt-2 grid gap-3 md:grid-cols-4">
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.form.amount')}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={draft.amount}
                      onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                      className={`${inputClass} w-full`}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.form.currency')}
                    </span>
                    <select
                      value={draft.currency}
                      onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                      className={`${inputClass} w-full`}
                    >
                      {CURRENCIES.map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.form.hours')}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={draft.hours}
                      onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
                      className={`${inputClass} w-full`}
                    />
                  </label>
                  <label className="block text-sm md:col-span-2">
                    <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                      {t('commercial:budget.form.noteLabel')}
                    </span>
                    <textarea
                      rows={2}
                      maxLength={2000}
                      value={draft.note}
                      onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                      className={`${inputClass} w-full`}
                    />
                  </label>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onRevisionSaved}
                    disabled={mutating}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {mutating
                      ? t('commercial:budget.form.saving')
                      : t('commercial:budget.form.action')}
                  </button>
                  {formError !== null && (
                    <p role="alert" className="text-sm text-blocked">
                      {formError}
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* ── Revision history — the server's newest-first order ──────── */}
            <section aria-label={t('commercial:budget.history.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
              <SectionLabel>{t('commercial:budget.history.title')}</SectionLabel>
              {history.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('commercial:budget.history.none')}
                </p>
              ) : (
                <ul className="mt-2 divide-y divide-border">
                  {history.map((revision) => (
                    <li key={revision.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
                      <span className="text-sm text-foreground">
                        #{revision.revision}
                        {revision.status === 'active' ? (
                          <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-[0.625rem] font-medium text-accent-foreground">
                            {t('commercial:budget.history.activeTag')}
                          </span>
                        ) : null}
                      </span>
                      <span className="v-figure text-sm tabular-nums text-foreground">
                        <ProjectionValue
                          value={revision.amountMinor}
                          format={(n) => formatMoney(n, revision.currency)}
                        />
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {revision.createdAt === undefined
                          ? '—'
                          : formatDay(new Date(revision.createdAt).toISOString().slice(0, 10))}
                      </span>
                      <span className="max-w-md text-xs text-muted-foreground">
                        {revision.note ?? '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <ConceptBadge />
        <p className="text-xs text-muted-foreground">{t('commercial:budget.demoNote')}</p>
      </div>
    </AppShell>
  )
}
